import { useState, useEffect, useRef, useCallback } from "react";
import client from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { Bell, Loader2, Clock, Briefcase, AlertTriangle, CalendarCheck, CheckCheck, Megaphone } from "lucide-react";

const ICON = {
  upcoming_shift: Clock, shift_reminder: Clock, day_notice: Megaphone, upcoming_job: Briefcase, missing_clock_out: AlertTriangle,
  overtime_pending: Clock, short_reason_needed: AlertTriangle, leave_decision: CalendarCheck,
  issue_open: AlertTriangle,
};
const SEV = { high: "text-red-600", medium: "text-amber-600", info: "text-slate-500" };

export function NotificationsBell() {
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const boxRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await client.get("/notifications");
      setItems(Array.isArray(data) ? data : []);
    } catch { /* noop */ }
  }, []);

  const dismissAlert = async (n, e) => {
    e.stopPropagation();
    try { await client.post(`/alerts/${n.ref}/dismiss`); await load(); } catch { /* noop */ }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 20000);
    return () => clearInterval(id);
  }, [load]);

  useEffect(() => {
    const onClick = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const unread = items.filter((i) => !i.read).length;

  const markAll = async () => {
    const keys = items.filter((i) => !i.read).map((i) => i.key);
    if (!keys.length) return;
    setBusy(true);
    try { await client.post("/notifications/read", { keys }); await load(); }
    catch { /* noop */ } finally { setBusy(false); }
  };

  return (
    <div ref={boxRef} className="relative" data-testid="notifications-bell">
      <button
        data-testid="notifications-bell-trigger"
        onClick={() => setOpen((o) => !o)}
        className="relative h-9 w-9 rounded-full hover:bg-slate-100 flex items-center justify-center"
      >
        <Bell className="h-5 w-5 text-slate-600" />
        {unread > 0 && (
          <span data-testid="notifications-unread-count" className="absolute -top-0.5 -right-0.5 h-4 min-w-[16px] px-1 rounded-full bg-brand text-white text-[10px] font-bold flex items-center justify-center">
            {unread}
          </span>
        )}
      </button>

      {open && (
        <div data-testid="notifications-panel" className="fixed sm:absolute left-2 right-2 sm:left-auto sm:right-0 top-14 sm:top-11 sm:w-80 bg-white rounded-xl shadow-xl border border-slate-200 overflow-hidden z-50">
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-100">
            <span className="text-sm font-semibold text-slate-900">{t("notif.title", "Notifications")}</span>
            <button data-testid="notifications-mark-all" onClick={markAll} disabled={busy || unread === 0}
              className="text-xs font-semibold text-brand hover:underline disabled:opacity-40 flex items-center gap-1">
              {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCheck className="h-3.5 w-3.5" />}{t("notif.markAll", "Mark all read")}
            </button>
          </div>
          <div className="max-h-[380px] overflow-y-auto">
            {items.length === 0 ? (
              <p className="px-4 py-6 text-sm text-slate-400 text-center" data-testid="notifications-empty">{t("notif.none", "You're all caught up.")}</p>
            ) : (
              items.map((n) => {
                const Icon = n.type === "alert" ? Bell : (ICON[n.type] || Bell);
                const aSev = n.type === "alert" ? { urgent: "text-red-600", warning: "text-amber-600", info: "text-slate-500" }[n.alertSeverity] : null;
                return (
                  <div key={n.key} data-testid="notification-item"
                    className={`flex items-start gap-2.5 px-4 py-2.5 border-b border-slate-50 ${n.read ? "opacity-60" : "bg-brand/5"}`}>
                    <Icon className={`h-4 w-4 mt-0.5 shrink-0 ${aSev || SEV[n.severity] || "text-slate-400"}`} />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm text-slate-800">{n.title}</p>
                      {n.body && <p className="text-xs text-slate-500 mt-0.5 line-clamp-3">{n.body}</p>}
                      <p className="text-[11px] text-slate-400 mt-0.5">{n.at}</p>
                    </div>
                    {n.dismissible ? (
                      <button data-testid="notification-dismiss" onClick={(e) => dismissAlert(n, e)}
                        className="text-[11px] font-semibold text-slate-400 hover:text-red-600 shrink-0">✕</button>
                    ) : (!n.read && <span className="h-2 w-2 rounded-full bg-brand mt-1.5 shrink-0" />)}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
